/**
 * Payment Calculator
 *
 * Gestisce tutti i calcoli relativi ai metodi di pagamento della sidebar
 * Pattern funzionale puro: input => output, nessuna mutazione di stato
 */

import { number, round, sum, subtract, smallerEq, largerEq } from 'mathjs'

/**
 * Calcola l'importo da pagare con carta di credito
 *
 * Logica:
 * 1. Se welfare copre tutto => ritorna 0
 * 2. Somma credito welfare + codici sconto
 * 3. Se questa somma copre il live_price => ritorna 0
 * 4. Altrimenti => ritorna il residuo arrotondato a 2 decimali
 *
 * @param {Object} config - Configurazione per il calcolo
 * @param {string} config.live_price - Prezzo corrente della prenotazione
 * @param {number} config.integration_credit - Credito welfare disponibile
 * @param {number} config.discount_codes_amount - Totale codici sconto applicati
 * @param {boolean} config.is_welfare - Se il welfare copre tutto (flag integration.welfare)
 * @returns {number} - Importo da pagare con carta (arrotondato a 2 decimali)
 */
export function calculate_credit_card_amount(config) {
  const { live_price, integration_credit, discount_codes_amount, is_welfare } = config

  // Se welfare copre tutto => no carta di credito
  if (is_welfare === true) return 0

  // Somma dei pagamenti alternativi (welfare + codici sconto)
  const total_other_payments = sum(integration_credit, discount_codes_amount)

  // Se i pagamenti alternativi coprono il prezzo => no carta
  if (largerEq(total_other_payments, number(live_price))) return 0

  // Calcola il residuo
  const residual = subtract(number(live_price), total_other_payments)

  return round(residual, 2)
}

/**
 * Calcola l'array dei metodi di pagamento disponibili
 *
 * Restituisce un array ordinato di metodi di pagamento con:
 * - Credito welfare (nascosto se ci sono punti)
 * - Punti fedeltà (mostrato solo se disponibili)
 * - Buoni sconto (nascosto se ci sono punti)
 * - Carta di credito (sempre visibile)
 *
 * @param {import('../types').PaymentCalculationContext} context - Contesto per il calcolo
 * @returns {import('../types').PaymentMethod[]} - Array dei metodi di pagamento
 */
export function calculate_payments(context) {
  const {
    live_price,
    integration_credit,
    discount_codes_amount,
    credit_card_amount,
    points,
    points_config,
  } = context

  const live_price_number = number(live_price)

  // Calcola credito welfare utilizzabile (minimo tra live_price e credito disponibile)
  const welfare_to_use = smallerEq(live_price_number, integration_credit)
    ? live_price_number
    : integration_credit

  // Calcola punti fedeltà utilizzabili
  const { total: points_total = 0, label: points_label = '' } = points_config || {}
  const points_to_use = smallerEq(points, points_total) ? points : points_total

  return [
    {
      name: 'Credito',
      value: welfare_to_use,
      info: '',
      type: 'welfare',
      show: !points_to_use, // nascondi se ci sono punti
    },
    {
      name: points_label || 'Punti fedeltà',
      value: points_to_use,
      info: '',
      type: 'points',
      show: !!points_to_use, // mostra solo se ci sono punti
    },
    {
      name: 'Buoni caricati',
      value: discount_codes_amount,
      info: '',
      type: 'discount_codes',
      show: !points_to_use, // nascondi se ci sono punti
    },
    {
      name: 'Carta di credito',
      value: credit_card_amount,
      info: '',
      type: 'credit_card',
      show: true,
    },
  ]
}

/**
 * Calcola la somma totale di tutti i metodi di pagamento
 *
 * @param {import('../types').PaymentMethod[]} payments - Array dei metodi di pagamento
 * @returns {number} - Somma totale dei pagamenti
 */
export function calculate_payments_sum(payments) {
  return payments.reduce((accumulator, payment) => {
    return sum(accumulator, payment.value)
  }, 0)
}